// OCR: Tesseract (Apache-2.0) through tesseract.js, fetched the first time it is needed from a pinned jsDelivr release, like the online fonts.
// It reads a picture of a page and returns the words with their boxes; the viewer turns them into an invisible text layer.

const SCRIPT = "https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist/tesseract.min.js";

export const OCR_LANGUAGES: Array<{ code: string; label: string }> = [
    { code: "eng", label: "English" }, { code: "spa", label: "Spanish" }, { code: "fra", label: "French" }, { code: "deu", label: "German" },
    { code: "ita", label: "Italian" }, { code: "por", label: "Portuguese" }, { code: "nld", label: "Dutch" }, { code: "hin", label: "Hindi" }
];

export interface OcrWord { text: string; x0: number; y0: number; x1: number; y1: number }

interface TesseractWorker {
    recognize(image: HTMLCanvasElement): Promise<{ data: { words?: Array<{ text: string; confidence: number; bbox: { x0: number; y0: number; x1: number; y1: number } }> } }>;
    terminate(): Promise<unknown>;
}
interface TesseractGlobal {
    createWorker(lang: string, oem?: number, opts?: { logger?: (m: { status: string; progress: number }) => void }): Promise<TesseractWorker>;
}

let loading: Promise<TesseractGlobal> | null = null;

function loadTesseract(): Promise<TesseractGlobal> {
    const have = (window as unknown as { Tesseract?: TesseractGlobal }).Tesseract;
    if (have) return Promise.resolve(have);
    loading ??= new Promise<TesseractGlobal>((resolve, reject) => {
        const s = document.createElement("script");
        s.src = SCRIPT;
        s.async = true;
        s.onload = () => { const t = (window as unknown as { Tesseract?: TesseractGlobal }).Tesseract; if (t) resolve(t); else reject(new Error("The text recogniser did not start.")); };
        s.onerror = () => { loading = null; reject(new Error("The text recogniser could not be downloaded. Check your connection and try again.")); };
        document.head.appendChild(s);
    });
    return loading;
}

/** A recogniser for one language; call `read` for each page picture, then `close`. */
export async function startOcr(lang: string, onStatus?: (status: string, fraction: number) => void): Promise<{ read(page: HTMLCanvasElement): Promise<OcrWord[]>; close(): Promise<void> }> {
    const T = await loadTesseract();
    const worker = await T.createWorker(lang, 1, { logger: (m) => onStatus?.(m.status, m.progress) });
    return {
        async read(canvas) {
            const { data } = await worker.recognize(canvas);
            return (data.words ?? []).filter((w) => w.text.trim() && w.confidence >= 30).map((w) => ({ text: w.text.trim(), x0: w.bbox.x0, y0: w.bbox.y0, x1: w.bbox.x1, y1: w.bbox.y1 }));
        },
        close: async () => { await worker.terminate(); }
    };
}
